import React, { useState, useEffect } from "react";
import { CalendarPlus, X, Disc3, Radio } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const minDate = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

/**
 * Lets a verified artist schedule an upcoming release.
 * - `collectionId` / `collectionName` (optional): binds the release to a
 *   specific album, "extending" it on the album page.
 * - `artworkUrl` (optional): inherits the album/artist artwork so the
 *   Coming Soon card looks polished.
 */
const ScheduleReleaseDialog = ({
  collectionId,
  collectionName,
  artworkUrl,
  artistName,
  triggerLabel,
  triggerClassName = "",
  onCreated,
  testId = "schedule-release-dialog",
}) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [type, setType] = useState("single");
  const [notes, setNotes] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setDate("");
      setType(collectionId ? "single" : "single");
      setNotes("");
      setSourceUrl("");
    }
  }, [open, collectionId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Track title is required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/upcoming/artist-submit", {
        release_title: title.trim(),
        expected_release_date: date || null,
        release_type: type,
        notes: notes.trim() || null,
        source_url: sourceUrl.trim() || null,
        collection_id: collectionId ? String(collectionId) : null,
        collection_name: collectionName || null,
        artwork_url: artworkUrl || null,
      });
      toast.success(
        collectionId
          ? `"${title}" scheduled on ${collectionName || "this album"}`
          : `"${title}" added to Coming Soon`
      );
      setOpen(false);
      onCreated?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule release");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`${testId}-trigger`}
          className={
            triggerClassName ||
            "inline-flex items-center gap-2 rounded-full bg-[#E2FF31]/15 text-[#E2FF31] border border-[#E2FF31]/30 px-4 py-2 text-sm font-semibold hover:bg-[#E2FF31]/25 transition"
          }
        >
          <CalendarPlus size={15} />
          {triggerLabel || (collectionId ? "Schedule on this album" : "Schedule upcoming release")}
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid={testId}
        className="bg-[#0a0a0a] border-white/10 text-white max-w-md p-0 overflow-hidden"
      >
        <DialogHeader className="px-6 py-4 border-b border-white/5 flex flex-row items-center justify-between">
          <DialogTitle className="flex items-center gap-2 font-display text-lg">
            <CalendarPlus size={18} className="text-[#E2FF31]" />
            {collectionId ? "Extend album" : "Schedule release"}
          </DialogTitle>
          <button
            type="button"
            data-testid={`${testId}-close`}
            onClick={() => setOpen(false)}
            className="p-1.5 rounded-full hover:bg-white/10 transition"
          >
            <X size={16} />
          </button>
        </DialogHeader>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {collectionId && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
              {artworkUrl ? (
                <img src={artworkUrl} alt="" className="w-10 h-10 rounded object-cover" />
              ) : (
                <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
                  <Disc3 size={16} className="text-white/60" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-white/40">
                  Adding to album
                </p>
                <p className="text-sm font-semibold truncate">{collectionName}</p>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">
              Track title <span className="text-[#FF2E00]">*</span>
            </label>
            <input
              data-testid={`${testId}-title`}
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={140}
              placeholder="What's the song called?"
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 transition"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">
                Release date
              </label>
              <input
                type="date"
                data-testid={`${testId}-date`}
                value={date}
                min={minDate()}
                onChange={(e) => setDate(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 transition [color-scheme:dark]"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">
                Type
              </label>
              <select
                data-testid={`${testId}-type`}
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 transition"
              >
                <option value="single">Single</option>
                <option value="EP">EP</option>
                <option value="album">Album</option>
                <option value="mixtape">Mixtape</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">
              Pre-save / teaser link
            </label>
            <input
              data-testid={`${testId}-source`}
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://… (optional)"
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 transition"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">
              Notes for fans <span className="text-white/30">(optional)</span>
            </label>
            <textarea
              data-testid={`${testId}-notes`}
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 500))}
              rows={3}
              placeholder="Anything fans should know about this drop"
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 transition resize-none"
            />
          </div>

          <div className="flex items-start gap-2 p-3 rounded-lg bg-[#E2FF31]/5 border border-[#E2FF31]/15 text-xs text-white/65">
            <Radio size={14} className="text-[#E2FF31] mt-0.5 flex-shrink-0" />
            <p>
              This shows up on <span className="text-white font-semibold">Coming Soon</span>
              {collectionId ? " and on this album's page" : " and on your artist page"} immediately. Fans tracking <span className="text-white font-semibold">{artistName || "you"}</span> get notified.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              data-testid={`${testId}-cancel`}
              onClick={() => setOpen(false)}
              className="text-sm text-white/60 hover:text-white px-4 py-2 rounded-full hover:bg-white/5 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid={`${testId}-submit`}
              disabled={busy || !title.trim()}
              className="bg-[#FF2E00] hover:bg-[#E62900] disabled:opacity-50 text-white font-semibold rounded-full px-5 py-2 text-sm transition flex items-center gap-2"
            >
              <CalendarPlus size={14} /> {busy ? "Scheduling…" : "Schedule"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleReleaseDialog;
